import { zodResolver } from "@hookform/resolvers/zod";
import { login } from "@stryker/core/api/accounts";
import type { LoginPayload } from "@stryker/core/types/accounts";
import { useMutation } from "@tanstack/react-query";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { Link } from "react-router-dom";
import { z } from "zod";
import EyeOffIcon from "@/assets/icons/eye-off.svg?react";
import EyeIcon from "@/assets/icons/eye-open.svg?react";
import MailIcon from "@/assets/icons/mail.svg?react";
import FullLogoImage from "@/assets/images/fullLogo.png";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { TextInput } from "@/components/ui/textInput";
import { Typography } from "@/components/ui/typography";
import useScreens from "@/hooks/useScreens";
import useAuthStore from "@/store/useAuthStore";
import { setAccessToken, setRefreshToken } from "@/utils/storageService";

const loginSchema = z.object({
  email: z.string().email({ message: "This is not a valid email" }).min(1, "Email is required"),
  password: z.string().min(1, "Password is required"),
});

type LoginSchema = z.infer<typeof loginSchema>;
export default function Login() {
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [loginError, setLoginError] = useState<string | null>(null);

  const { setIsAuthorized } = useAuthStore();
  const isTabletOrMore = useScreens("md", "up");

  const form = useForm<LoginSchema>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: "",
      password: "",
    },
  });

  const { mutate, isPending } = useMutation({
    mutationFn: (data: LoginPayload) => login(data),
    onSuccess: (response) => {
      setLoginError(null);
      setAccessToken(response.access);
      setIsAuthorized(true);
      if (rememberMe) {
        setRefreshToken(response.refresh);
      }
    },
    onError: (error: string) => {
      setLoginError(error);
    },
  });

  const onLogin = (data: LoginPayload) => {
    setLoginError(null);
    mutate(data);
  };

  const openEmailClient = () => {
    window.open("mailto:support@stryker.xyz?Subject=Help", "_blank");
  };

  return (
    <div className="flex h-screen w-full flex-col">
      {isTabletOrMore && (
        <img alt="Stryker Industrial Solutions" src={FullLogoImage} width="144px" height="18px" className="mt-6 ml-6" />
      )}
      <div className="mt-auto flex w-[360px] flex-col self-center">
        {!isTabletOrMore && (
          <img
            alt="Stryker Industrial Solutions"
            src={FullLogoImage}
            width="144px"
            height="18px"
            className="mb-8 self-center"
          />
        )}
        <Typography variant="display-lg" weight="semibold" className="text-center md:text-left">
          Log in
        </Typography>
        <Typography variant="text-md" weight="medium" className="mt-3 mb-8 text-gray-600">
          Welcome back! Please enter your login details.
        </Typography>
        <Form {...form}>
          <form className="md:w-80" onSubmit={form.handleSubmit(onLogin)}>
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem className="mb-4 flex flex-1 flex-col">
                  <FormLabel className="text-left">Email</FormLabel>
                  <FormControl>
                    <TextInput
                      placeholder="Email"
                      {...field}
                      type="email"
                      onChange={(event) => {
                        if (loginError) {
                          setLoginError(null);
                        }
                        field.onChange(event);
                      }}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="password"
              render={({ field }) => (
                <FormItem className="flex flex-1 flex-col">
                  <FormLabel className="text-left">Password</FormLabel>
                  <FormControl>
                    <TextInput
                      placeholder="Password"
                      {...field}
                      endAdornment={
                        showPassword ? (
                          <EyeOffIcon onClick={() => setShowPassword(false)} />
                        ) : (
                          <EyeIcon onClick={() => setShowPassword(true)} />
                        )
                      }
                      type={showPassword ? "text" : "password"}
                      onChange={(event) => {
                        if (loginError) {
                          setLoginError(null);
                        }
                        field.onChange(event);
                      }}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <div className="mt-9 mb-9 flex items-center justify-between">
              <Checkbox
                checked={rememberMe}
                onChange={(value) => setRememberMe(value)}
                label="Remember Me"
                id="rememberMe"
              />
              <Link to="/forgot-password">
                <Typography variant="text-small" weight="semibold" className="primary-700">
                  Forgot password?
                </Typography>
              </Link>
            </div>
            {loginError ? (
              <Typography variant="text-small" className="mb-4 text-red-600">
                {loginError}
              </Typography>
            ) : null}
            <Button disabled={isPending} className="w-full" size="lg" type="submit">
              Log in
            </Button>
          </form>
        </Form>
      </div>
      <div className="mt-auto flex items-center justify-between px-8">
        <Typography>&copy; Stryker Industrial Solutions,LLC</Typography>
        <Button onClick={openEmailClient} className="flex items-center gap-2" variant="ghost">
          <MailIcon />
          support@stryker.xyz
        </Button>
      </div>
    </div>
  );
}
